import { memo, useCallback, useMemo, useReducer } from 'react'
import PropTypes from 'prop-types'
import ModalPortal from './ModalPortal.jsx'
import { useCloudContext } from '../contexts/CloudProvider.jsx'

// Move components outside to prevent re-creation on every render
const ProfileSection = memo(({ title, icon, children, className = '' }) => (
  <div className={`mb-4 ${className}`}>
    <h4 className="mb-3" style={{
      fontSize: '1.125rem',
      fontWeight: '600',
      color: 'var(--text-medium)',
      borderBottom: '1px solid var(--border-color-light-gray)',
      paddingBottom: '8px',
    }}>
      <i className={`bi ${icon} me-2`}></i>
      {title}
    </h4>
    {children}
  </div>
))
ProfileSection.displayName = 'ProfileSection'

const FormInput = memo(({ 
  type = 'text', 
  id, 
  label, 
  value, 
  onChange, 
  placeholder, 
  required = false, 
  disabled = false, 
  autoComplete,
  minLength,
  maxLength,
  helpText,
}) => (
  <div className="mb-3">
    <label htmlFor={id} className="form-label" style={{ 
      fontSize: '0.875rem', 
      fontWeight: '600',
      color: 'var(--text-medium)',
      marginBottom: '8px',
    }}>
      {label}
    </label>
    <input
      type={type}
      className="form-control"
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      autoComplete={autoComplete}
      minLength={minLength}
      maxLength={maxLength}
      style={{ 
        padding: '12px 16px', 
        fontSize: '0.875rem',
        borderRadius: '8px',
        border: '1px solid var(--border-color-strong)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: disabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
      }}
      onFocus={(e) => {
        if (!disabled) {
          e.target.style.borderColor = 'var(--color-focus-blue)'
          e.target.style.boxShadow = '0 0 0 3px var(--color-focus-blue-shadow)'
        }
      }}
      onBlur={(e) => {
        e.target.style.borderColor = 'var(--border-color-strong)'
        e.target.style.boxShadow = 'none'
      }}
    />
    {helpText && (
      <div className="form-text" style={{ fontSize: '0.75rem', color: 'var(--text-light-muted)' }}>
        {helpText}
      </div>
    )}
  </div>
))
FormInput.displayName = 'FormInput'

const ActionButton = memo(({ 
  type = 'button', 
  variant = 'primary', 
  onClick, 
  disabled = false, 
  loading = false, 
  loadingText = 'Loading...', 
  icon, 
  children, 
  className = '',
}) => {
  const variantConfig = {
    primary: { 
      classes: 'btn-primary',
      customStyle: { backgroundColor: 'var(--btn-primary-bg)', borderColor: 'var(--btn-primary-border)' },
      hoverStyle: { backgroundColor: 'var(--btn-primary-hover-bg)', borderColor: 'var(--btn-primary-hover-border)', transform: 'translateY(-1px)' },
    },
    success: { 
      classes: 'btn-success',
      customStyle: { backgroundColor: 'var(--btn-success-bg)', borderColor: 'var(--btn-success-border)' },
      hoverStyle: { backgroundColor: 'var(--btn-success-hover-bg)', borderColor: 'var(--btn-success-hover-border)', transform: 'translateY(-1px)' },
    },
    danger: { 
      classes: 'btn-danger',
      customStyle: { backgroundColor: 'var(--btn-danger-bg)', borderColor: 'var(--btn-danger-border)' },
      hoverStyle: { backgroundColor: 'var(--btn-danger-hover-bg)', borderColor: 'var(--btn-danger-hover-border)', transform: 'translateY(-1px)' },
    },
    'outline-primary': { 
      classes: 'btn-outline-primary',
      customStyle: { borderColor: 'var(--btn-outline-primary-border)', color: 'var(--btn-outline-primary-color)' },
      hoverStyle: { backgroundColor: 'var(--btn-primary-bg)', borderColor: 'var(--btn-primary-border)', color: 'var(--text-on-dark-bg)', transform: 'translateY(-1px)' },
    },
    'outline-secondary': { 
      classes: 'btn-outline-secondary',
      customStyle: {},
      hoverStyle: { transform: 'translateY(-1px)' },
    },
    'outline-danger': { 
      classes: 'btn-outline-danger',
      customStyle: { borderColor: 'var(--btn-outline-danger-border)', color: 'var(--btn-outline-danger-color)' },
      hoverStyle: { backgroundColor: 'var(--btn-danger-bg)', borderColor: 'var(--btn-danger-border)', color: 'var(--text-on-dark-bg)', transform: 'translateY(-1px)' },
    },
  }

  const config = variantConfig[variant] || variantConfig.primary

  return (
    <button
      type={type}
      className={`btn ${config.classes} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        ...config.customStyle,
        fontSize: '0.875rem',
        borderRadius: '8px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        fontWeight: '500',
        boxShadow: 'var(--shadow-sm)',
      }}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.target.style, {
            ...config.hoverStyle,
            boxShadow: 'var(--shadow-md)',
          })
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.target.style, {
            ...config.customStyle,
            fontSize: '0.875rem',
            borderRadius: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            fontWeight: '500',
            boxShadow: 'var(--shadow-sm)',
            transform: 'translateY(0)',
          })
        }
      }}
      onMouseDown={(e) => {
        if (!disabled && !loading) {
          e.target.style.transform = 'translateY(0) scale(0.98)'
        }
      }}
      onMouseUp={(e) => {
        if (!disabled && !loading) {
          e.target.style.transform = 'translateY(-1px) scale(1)'
        }
      }}
    >
      {loading ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {loadingText}
        </>
      ) : (
        <>
          {icon && <i className={`bi ${icon} me-2`}></i>}
          {children}
        </>
      )}
    </button>
  )
})
ActionButton.displayName = 'ActionButton'

const Alert = memo(({ type = 'info', children }) => {
  const styles = {
    danger: { backgroundColor: 'var(--alert-danger-bg)', color: 'var(--alert-danger-color)', icon: 'bi-exclamation-triangle' },
    success: { backgroundColor: 'var(--alert-success-bg)', color: 'var(--alert-success-color)', icon: 'bi-check-circle' },
    info: { backgroundColor: 'var(--alert-info-bg)', color: 'var(--alert-info-color)', icon: 'bi-info-circle' },
  }

  return (
    <div 
      className={`alert alert-${type}`} 
      style={{ 
        padding: '12px 16px',
        fontSize: '0.875rem',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: styles[type].backgroundColor,
        color: styles[type].color,
        marginBottom: '16px',
      }}
    >
      <i className={`bi ${styles[type].icon} me-2`}></i>
      {children}
    </div>
  )
})
Alert.displayName = 'Alert'

// Unified state reducer for better performance and cleaner code
const profileReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'SET_MULTIPLE':
      return { ...state, ...action.payload }
    case 'RESET_SECTION':
      return { ...state, ...action.payload }
    case 'CLEAR_MESSAGES':
      return { ...state, message: '', error: '' }
    default:
      return state
  }
}

const initialState = {
  displayName: '',
  loading: false,
  message: '',
  error: '',
  // Email states
  isChangingEmail: false,
  newEmail: '',
  currentPassword: '',
  emailLoading: false,
  // Password states
  isChangingPassword: false,
  currentPasswordForPasswordChange: '',
  newPassword: '',
  confirmPassword: '',
  passwordLoading: false,
  // Deletion states
  isConfirmingDeletion: false,
  deletePassword: '',
  deleteConfirmText: '',
  deleteLoading: false,
}

/**
 * UserProfile Modal component optimized for React 19+
 * Features unified state management, optimized re-renders, and DRY principles
 * Now wrapped in a modal for better UX integration
 */
const UserProfile = memo(({ isOpen, onClose }) => {
  const { user, updateUserProfile, changeEmail, changePassword, deleteAccount } = useCloudContext()
  const [state, dispatch] = useReducer(profileReducer, {
    ...initialState,
    displayName: user?.displayName || '',
  })

  // Memoized validation functions
  const validators = useMemo(() => ({
    displayName: (value) => {
      if (!value?.trim()) return 'Please enter your full name'
      if (value.trim().length < 2) return 'Name must be at least 2 characters long'
      return null
    },
    email: (value) => {
      if (!value?.trim()) return 'Please enter an email address'
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address'
      if (value === user?.email) return 'New email address must be different from current email'
      return null
    },
    password: (current, newPass, confirm) => {
      if (!current?.trim() || !newPass?.trim() || !confirm?.trim()) return 'Please fill in all password fields'
      if (newPass !== confirm) return 'New passwords do not match'
      if (newPass.length < 6) return 'New password must be at least 6 characters long'
      if (newPass === current) return 'New password must be different from current password'
      return null
    },
  }), [user?.email])

  // Optimized handlers with useCallback
  const setField = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value })
  }, [])

  const handleSaveProfile = useCallback(async (e) => {
    e.preventDefault()
    const error = validators.displayName(state.displayName)
    if (error) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: error })
      return
    }

    dispatch({ type: 'SET_MULTIPLE', payload: { loading: true, error: '', message: '' } })
    
    try {
      await updateUserProfile({ displayName: state.displayName.trim() })
      dispatch({ type: 'SET_FIELD', field: 'message', value: 'Profile updated successfully!' })
    } catch (error) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: error.message })
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'loading', value: false })
    }
  }, [state.displayName, validators, updateUserProfile])

  const handleEmailChange = useCallback(async (e) => {
    e.preventDefault()
    const emailError = validators.email(state.newEmail)
    if (emailError) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: emailError })
      return
    }
    if (!state.currentPassword.trim()) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: 'Please enter your current password' })
      return
    }

    dispatch({ type: 'SET_MULTIPLE', payload: { emailLoading: true, error: '', message: '' } })

    try {
      const result = await changeEmail(state.newEmail, state.currentPassword)
      if (result.success) {
        dispatch({ 
          type: 'RESET_SECTION', 
          payload: { 
            message: result.message, 
            isChangingEmail: false, 
            newEmail: '', 
            currentPassword: '', 
            emailLoading: false,
          },
        })
      }
    } catch (error) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: error.message })
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'emailLoading', value: false })
    }
  }, [state.newEmail, state.currentPassword, validators, changeEmail])

  const handlePasswordChange = useCallback(async (e) => {
    e.preventDefault()
    const passwordError = validators.password(
      state.currentPasswordForPasswordChange, 
      state.newPassword, 
      state.confirmPassword,
    )
    if (passwordError) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: passwordError })
      return
    }

    dispatch({ type: 'SET_MULTIPLE', payload: { passwordLoading: true, error: '', message: '' } })

    try {
      const result = await changePassword(state.currentPasswordForPasswordChange, state.newPassword)
      if (result.success) {
        dispatch({ 
          type: 'RESET_SECTION', 
          payload: { 
            message: result.message, 
            isChangingPassword: false, 
            currentPasswordForPasswordChange: '', 
            newPassword: '', 
            confirmPassword: '', 
            passwordLoading: false,
          },
        })
      }
    } catch (error) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: error.message })
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'passwordLoading', value: false })
    }
  }, [state.currentPasswordForPasswordChange, state.newPassword, state.confirmPassword, validators, changePassword])

  const handleAccountDeletion = useCallback(async (e) => {
    e.preventDefault()
    if (!state.deletePassword.trim()) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: 'Please enter your password to confirm account deletion' })
      return
    }
    if (state.deleteConfirmText !== 'DELETE') {
      dispatch({ type: 'SET_FIELD', field: 'error', value: 'Please type "DELETE" to confirm account deletion' })
      return
    }

    dispatch({ type: 'SET_MULTIPLE', payload: { deleteLoading: true, error: '', message: '' } })

    try {
      const result = await deleteAccount(state.deletePassword)
      if (result.success) {
        dispatch({ type: 'SET_FIELD', field: 'message', value: result.message })
      }
    } catch (error) {
      dispatch({ type: 'SET_FIELD', field: 'error', value: error.message })
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'deleteLoading', value: false })
    }
  }, [state.deletePassword, state.deleteConfirmText, deleteAccount])

  const cancelEmailChange = useCallback(() => {
    dispatch({ 
      type: 'RESET_SECTION', 
      payload: { 
        isChangingEmail: false, 
        newEmail: '', 
        currentPassword: '', 
        error: '', 
        message: '',
      },
    })
  }, [])

  const cancelPasswordChange = useCallback(() => {
    dispatch({ 
      type: 'RESET_SECTION', 
      payload: { 
        isChangingPassword: false, 
        currentPasswordForPasswordChange: '', 
        newPassword: '', 
        confirmPassword: '', 
        error: '', 
        message: '',
      },
    })
  }, [])

  const cancelAccountDeletion = useCallback(() => {
    dispatch({ 
      type: 'RESET_SECTION', 
      payload: { 
        isConfirmingDeletion: false, 
        deletePassword: '', 
        deleteConfirmText: '', 
        error: '', 
        message: '',
      },
    })
  }, [])

  // Early return for unauthenticated users
  if (!user) {
    return (
      <ModalPortal
        isOpen={isOpen}
        onClose={onClose}
        size="large"
        animation="slideUp"
        zIndex="var(--z-modal)"
      >
        <div className="text-center p-4">
          <p>Please sign in to view your profile.</p>
          <button 
            className="btn btn-primary mt-2" 
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </ModalPortal>
    )
  }

  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      animation="slideUp"
      zIndex="var(--z-modal)"
    >
      <div style={{ maxHeight: 'calc(100vh - 80px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Modal Header */}
        <div 
          style={{
            padding: '20px 24px 16px 24px',
            borderBottom: '1px solid var(--border-color-light-gray)',
            background: 'var(--bg-header-gradient)',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: 'var(--text-dark)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <i className="bi bi-person-circle"></i>
            User Account
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--text-light-muted)',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.color = 'var(--text-medium)'
              e.target.style.backgroundColor = 'var(--color-hover-dark)'
            }}
            onMouseLeave={(e) => {
              e.target.style.color = 'var(--text-light-muted)'
              e.target.style.backgroundColor = 'transparent'
            }}
            aria-label="Close"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '24px',
          background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-light-blue) 100%)',
        }}>
          <div>
            {/* Profile Information */}
            <div className="mb-4">
              <div className="row mb-3">
                <div className="col-4"><strong style={{ color: 'var(--text-medium)' }}>Email:</strong></div>
                <div className="col-8"><span style={{ color: 'var(--text-light-muted)' }}>{user.email}</span></div>
              </div>
              <div className="row mb-3">
                <div className="col-4"><strong style={{ color: 'var(--text-medium)' }}>Email Verified:</strong></div>
                <div className="col-8">
                  <span className={`badge ${user.emailVerified ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: '0.75rem' }}>
                    {user.emailVerified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-4"><strong style={{ color: 'var(--text-medium)' }}>Account Created:</strong></div>
                <div className="col-8">
                  <span style={{ color: 'var(--text-light-muted)' }}>
                    {user.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

              {/* Messages */}
              {state.error && <Alert type="danger">{state.error}</Alert>}
              {state.message && <Alert type="success">{state.message}</Alert>}

              {/* Profile Management */}
              <ProfileSection title="Profile Information" icon="bi-person">
                <form onSubmit={handleSaveProfile}>
                  <FormInput
                    id="displayName"
                    label="Full Name"
                    value={state.displayName}
                    onChange={(e) => setField('displayName', e.target.value)}
                    placeholder="Enter your full name"
                    required
                    disabled={state.loading}
                    minLength={2}
                    maxLength={50}
                  />
                  <ActionButton
                    type="submit"
                    variant="primary"
                    disabled={state.loading}
                    loading={state.loading}
                    loadingText="Saving..."
                  >
                    Save Profile
                  </ActionButton>
                </form>
              </ProfileSection>

              {/* Email Management */}
              <ProfileSection title="Email Management" icon="bi-envelope">
                {!state.isChangingEmail ? (
                  <div>
                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                      Update your email address. You&apos;ll need to verify the new address before it becomes active.
                    </p>
                    <ActionButton
                      variant="primary"
                      onClick={() => setField('isChangingEmail', true)}
                      icon="bi-pencil"
                    >
                      Change Email Address
                    </ActionButton>
                  </div>
                ) : (
                  <form onSubmit={handleEmailChange}>
                    <FormInput
                      type="email"
                      id="currentEmail"
                      label="Current Email"
                      value={user?.email || ''}
                      disabled
                    />
                    <FormInput
                      type="email"
                      id="newEmail"
                      label="New Email Address"
                      value={state.newEmail}
                      onChange={(e) => setField('newEmail', e.target.value)}
                      placeholder="your-new@email.com"
                      required
                      disabled={state.emailLoading}
                      autoComplete="email"
                    />
                    <FormInput
                      type="password"
                      id="confirmPassword"
                      label="Current Password"
                      value={state.currentPassword}
                      onChange={(e) => setField('currentPassword', e.target.value)}
                      placeholder="Enter your current password"
                      required
                      disabled={state.emailLoading}
                      autoComplete="current-password"
                      helpText="For security, we need to verify your current password."
                    />
                    <div className="d-flex gap-2">
                      <ActionButton
                        type="submit"
                        variant="primary"
                        disabled={state.emailLoading || !state.newEmail.trim() || !state.currentPassword.trim()}
                        loading={state.emailLoading}
                        loadingText="Updating Email..."
                        icon="bi-check-lg"
                      >
                        Update Email
                      </ActionButton>
                      <ActionButton
                        variant="outline-secondary"
                        onClick={cancelEmailChange}
                        disabled={state.emailLoading}
                      >
                        Cancel
                      </ActionButton>
                    </div>
                  </form>
                )}
              </ProfileSection>

              {/* Password Management */}
              <ProfileSection title="Password Management" icon="bi-key">
                {!state.isChangingPassword ? (
                  <div>
                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                      Change your account password. You&apos;ll need to confirm your current password.
                    </p>
                    <ActionButton
                      variant="primary"
                      onClick={() => setField('isChangingPassword', true)}
                      icon="bi-key"
                    >
                      Change Password
                    </ActionButton>
                  </div>
                ) : (
                  <form onSubmit={handlePasswordChange}>
                    <FormInput
                      type="password"
                      id="currentPasswordForPasswordChange"
                      label="Current Password"
                      value={state.currentPasswordForPasswordChange}
                      onChange={(e) => setField('currentPasswordForPasswordChange', e.target.value)}
                      placeholder="Enter your current password"
                      required
                      disabled={state.passwordLoading}
                      autoComplete="current-password"
                    />
                    <FormInput
                      type="password"
                      id="newPassword"
                      label="New Password"
                      value={state.newPassword}
                      onChange={(e) => setField('newPassword', e.target.value)}
                      placeholder="Enter your new password"
                      required
                      disabled={state.passwordLoading}
                      autoComplete="new-password"
                      minLength={6}
                      helpText="Password must be at least 6 characters long."
                    />
                    <FormInput
                      type="password"
                      id="confirmPassword"
                      label="Confirm New Password"
                      value={state.confirmPassword}
                      onChange={(e) => setField('confirmPassword', e.target.value)}
                      placeholder="Confirm your new password"
                      required
                      disabled={state.passwordLoading}
                      autoComplete="new-password"
                    />
                    <div className="d-flex gap-2">
                      <ActionButton
                        type="submit"
                        variant="primary"
                        disabled={state.passwordLoading || !state.currentPasswordForPasswordChange.trim() || !state.newPassword.trim() || !state.confirmPassword.trim()}
                        loading={state.passwordLoading}
                        loadingText="Updating Password..."
                        icon="bi-check-lg"
                      >
                        Update Password
                      </ActionButton>
                      <ActionButton
                        variant="outline-secondary"
                        onClick={cancelPasswordChange}
                        disabled={state.passwordLoading}
                      >
                        Cancel
                      </ActionButton>
                    </div>
                  </form>
                )}
              </ProfileSection>

              {/* Account Deletion */}
              <ProfileSection title="Danger Zone" icon="bi-exclamation-triangle" className="danger-zone" style={{ color: 'var(--bs-danger)' }}>
                {!state.isConfirmingDeletion ? (
                  <div>
                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                      <strong>Warning:</strong> Account deletion is permanent and cannot be undone. 
                      All your data, devices, and settings will be permanently removed.
                    </p>
                    <ActionButton
                      variant="danger"
                      onClick={() => setField('isConfirmingDeletion', true)}
                      icon="bi-trash"
                    >
                      Delete Account
                    </ActionButton>
                  </div>
                ) : (
                  <div className="alert alert-danger" style={{ padding: '16px', borderRadius: '8px' }}>
                    <h6 className="alert-heading">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Confirm Account Deletion
                    </h6>
                    <p className="mb-3">
                      This action cannot be undone. This will permanently delete your account and remove all associated data.
                    </p>
                    
                    <form onSubmit={handleAccountDeletion}>
                      <FormInput
                        type="password"
                        id="deletePassword"
                        label="Enter your current password to confirm"
                        value={state.deletePassword}
                        onChange={(e) => setField('deletePassword', e.target.value)}
                        placeholder="Enter your current password"
                        required
                        disabled={state.deleteLoading}
                        autoComplete="current-password"
                        helpText="Use your current password (the one you use to sign in now)."
                      />
                      <FormInput
                        type="text"
                        id="deleteConfirmText"
                        label='Type "DELETE" to confirm'
                        value={state.deleteConfirmText}
                        onChange={(e) => setField('deleteConfirmText', e.target.value)}
                        placeholder="Type DELETE"
                        required
                        disabled={state.deleteLoading}
                      />
                      <div className="d-flex gap-2">
                        <ActionButton
                          type="submit"
                          variant="danger"
                          disabled={state.deleteLoading || !state.deletePassword.trim() || state.deleteConfirmText !== 'DELETE'}
                          loading={state.deleteLoading}
                          loadingText="Deleting Account..."
                          icon="bi-trash"
                        >
                          Delete My Account
                        </ActionButton>
                        <ActionButton
                          variant="outline-secondary"
                          onClick={cancelAccountDeletion}
                          disabled={state.deleteLoading}
                        >
                          Cancel
                        </ActionButton>
                      </div>
                    </form>
                  </div>
                )}
              </ProfileSection>

              {/* Account Details */}
              <ProfileSection title="Account Details" icon="bi-info-circle">
                <div className="row mb-2">
                  <div className="col-4">
                    <strong style={{ color: 'var(--text-medium)', fontSize: '0.875rem' }}>Last Sign In:</strong>
                  </div>
                  <div className="col-8">
                    <span style={{ color: 'var(--text-light-muted)', fontSize: '0.875rem' }}>
                      {user.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              </ProfileSection>

              {/* Security Information */}
              <ProfileSection title="Security Information" icon="bi-shield-check">
                <Alert type="info">
                  <strong>Security Tips:</strong>
                  <ul className="mb-0 mt-2" style={{ fontSize: '0.875rem' }}>
                    <li>Always verify your identity when changing account settings</li>
                    <li>Check your email for verification messages after making changes</li>
                    <li>Use a strong, unique password for your account</li>
                    <li>Sign out and sign back in if you see authentication errors</li>
                  </ul>
                </Alert>
              </ProfileSection>
            </div>
          </div>
        </div>
      </ModalPortal>
    )
})

UserProfile.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

UserProfile.displayName = 'UserProfile'

export default UserProfile
